import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import withLifecycle from '@hocs/with-lifecycle';
import { compose } from 'recompose';

import { components as clientComponents } from 'mage-client';
import { translator } from 'mage-i18n';
import { selectors as coreSelectors, components as coreComponents } from 'mage-core';
import { MOET_CONSTANTS } from 'moet-core';
import { Column, GridContainer, Row, VerticalRhythm, withResponsive } from 'mage-components';

import {
  getMerchantStatus,
  getUserMerchantId,
  hasHadTransaction,
  hasNewIntegrationGuidesAvailable,
  merchantRegion,
} from '@/selectors/merchant';
import { getFeatureByKey } from '@/selectors/featuresConfig';
import { CAPS, FEATURES } from '@/constants';
import { PhoneCollectionBlock } from '@/components/PhoneCollectionBlock';
import { MidSelector } from '@/containers/MidSelector';

import ApiKeyContainer from './components/ApiKeyContainer';
import Review from './components/Review';
import Rejection from './components/Rejection';
import Disabled from './components/Disabled';
import Completed from './components/Completed';
import { Connection, ConnectionWithNewIntegrationGuides } from './components/Connection';
import WhatsNextTimeline from './components/WhatsNextTimeline';

const { ONBOARDING_STATUS_VALUES_V2, ONBOARDING_STATUSES_V2 } = MOET_CONSTANTS;

const tPrefix = 'products-fe.pages.whats_next_page';

const shouldShowConnectStore = merchantStatus =>
  ONBOARDING_STATUS_VALUES_V2[merchantStatus] >= ONBOARDING_STATUS_VALUES_V2.TRANSACTIONS_ENABLED;

const WhatsNextPage = ({
  basePath,
  hasHadTransaction,
  shouldUseNewIntegrationGuides,
  history,
  merchantStatus,
  mids,
  showPhoneNumberCollection,
  t,
}) => {
  let Content = () => null;

  if (
    [
      ONBOARDING_STATUSES_V2.TRANSACTIONS_REVIEW_AUTOMATED,
      ONBOARDING_STATUSES_V2.TRANSACTIONS_REVIEW_MANUAL,
    ].includes(merchantStatus)
  ) {
    Content = Review;
  } else if (
    ONBOARDING_STATUS_VALUES_V2[merchantStatus] >=
      ONBOARDING_STATUS_VALUES_V2.TRANSACTIONS_ENABLED &&
    hasHadTransaction
  ) {
    Content = Completed;
  } else if (
    ONBOARDING_STATUS_VALUES_V2[merchantStatus] >= ONBOARDING_STATUS_VALUES_V2.TRANSACTIONS_ENABLED
  ) {
    Content = shouldUseNewIntegrationGuides ? ConnectionWithNewIntegrationGuides : Connection;
  } else if (
    ONBOARDING_STATUS_VALUES_V2[merchantStatus] ===
    ONBOARDING_STATUS_VALUES_V2[ONBOARDING_STATUSES_V2.SUSPENDED]
  ) {
    Content = Rejection;
  } else if (
    ONBOARDING_STATUS_VALUES_V2[merchantStatus] ===
    ONBOARDING_STATUS_VALUES_V2[ONBOARDING_STATUSES_V2.TRANSACTIONS_DISABLED]
  ) {
    Content = Disabled;
  }

  return (
    <GridContainer>
      <Row spacing={{ mobile: 0 }} extraStyles={{ paddingTop: '5px' }}>
        <Column columns={{ mobile: 12, tablet: 7 }} spacing={{ tablet: 2, desktop: 0 }}>
          {mids?.length > 1 ? (
            <>
              <MidSelector />
              <VerticalRhythm spacing={{ mobile: 5, tablet: 9 }} />
            </>
          ) : null}
          <Content t={t} />
        </Column>
        <Column columns={{ desktop: 1 }} display={{ mobile: 'none', tablet: 'none' }}>
          &nbsp;
        </Column>
        <Column columns={{ mobile: 12, tablet: 5, desktop: 4 }} spacing={{ tablet: 2, desktop: 0 }}>
          <WhatsNextTimeline basePath={basePath} />

          <VerticalRhythm spacing={{ mobile: 4 }} />

          {shouldShowConnectStore(merchantStatus) && (
            <ApiKeyContainer isNewLayout tPrefix={tPrefix} />
          )}

          {showPhoneNumberCollection && <PhoneCollectionBlock />}
        </Column>
      </Row>
    </GridContainer>
  );
};

WhatsNextPage.propTypes = {
  basePath: PropTypes.string.isRequired,
  hasHadTransaction: PropTypes.bool.isRequired,
  history: PropTypes.object.isRequired,
  merchantStatus: PropTypes.string.isRequired,
  mids: PropTypes.array.isRequired,
  shouldUseNewIntegrationGuides: PropTypes.bool.isRequired,
  showPhoneNumberCollection: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
};

const WrappedComponent = compose(
  coreComponents.withTokenHelper(tokenHelper => ({
    tokenHelper,
  })),
  clientComponents.withPromiseBasedCapabilities({
    getFirstTransaction: CAPS.V2_MERCHANTS_FIRST_TRANSACTION_GET,
  }),
  connect((state, { tokenHelper }) => ({
    hasHadTransaction: hasHadTransaction(state),
    merchantRegion: merchantRegion(state),
    merchantStatus: getMerchantStatus(state),
    t: translator(state),
    mid: getUserMerchantId(state),
    mids: coreSelectors.getMerchantIds(state),
    shouldUseNewIntegrationGuides: hasNewIntegrationGuidesAvailable(state)(tokenHelper),
    showPhoneNumberCollection: getFeatureByKey(FEATURES.PHONE_NUMBER_COLLECTION)(state),
  })),
  withLifecycle({
    async onDidMount({ getFirstTransaction, mid, merchantRegion }) {
      await getFirstTransaction({ params: { mid }, region: merchantRegion });
    },
  }),
)(withResponsive(WhatsNextPage));

// TODO: Create a "StepVariant" HOC that accepts these as options.
// Declarative definition of where the variant is used
WrappedComponent.onboardingTypes = '*';
WrappedComponent.deploymentRegionsAndCountries = {
  eu: ['fallback'],
  us: ['fallback'],
  ap: ['fallback'],
};
WrappedComponent.fieldsToFill = [];

export default WrappedComponent;
